import React from 'react';
import styles from './RefundPolicy.module.css';

const RefundPolicy = () => {
  return (
    <div className={styles.refundPage}>
      {/* Header */}
      <div className={styles.refundHeader}>
        <div className={styles.container}>
          <h1>Refund <span className={styles.gradientText}>Policy</span></h1>
          <p>Last Updated: July 10, 2021</p>
        </div>
        <div className={styles.headerWave}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path fill="#EDFFF7" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.refundContent}>
          
          <div className={styles.lastUpdated}>
            <p>This Refund Policy explains the terms and conditions under which Exademy Education Services ("Company," "we," "our," or "us") provides refunds for courses, test series, mentorship programs, study materials, and other services purchased through our website, mobile application, or offline centers.</p>
            <p className={styles.emphasis}>We want you to be completely satisfied with your learning experience. This policy is designed to be fair and transparent while protecting the integrity of our educational content and services. Please read this Refund Policy carefully before making any purchase.</p>
          </div>

          {/* Section 1 - Introduction */}
          <div className={styles.refundSection}>
            <h2>1. Introduction and Scope</h2>
            
            <p>Exademy is an educational technology platform headquartered in Bhagalpur, Bihar, India, with additional operations in Delhi and Patna. We provide online and offline coaching, study materials, test series, and mentorship programs primarily for Mathematics Optional preparation for UPSC Civil Services Examination, BPSC State Services Examination, CSIR NET, GATE, and other competitive examinations.</p>
            
            <p>This Refund Policy applies to all purchases made on or after February 15, 2026. For purchases made before this date, the refund policy that was in effect at the time of purchase shall apply. This policy covers all products and services offered by Exademy, including but not limited to:</p>
            
            <ul>
              <li>UPSC Mathematics Optional courses (2026 and 2027 batches)</li>
              <li>BPSC Mathematics Optional courses</li>
              <li>CSIR NET and GATE Mathematics courses</li>
              <li>Mathematics Foundation courses</li>
              <li>Chapter-wise Test Series</li>
              <li>Full Syllabus Test Series</li>
              <li>Mentorship and Guidance Programs</li>
              <li>Printed study materials and books</li>
              <li>Digital downloads and PDF notes</li>
              <li>Recorded video lectures and bundles</li>
              <li>Doubt clearing packages</li>
              <li>Answer writing evaluation services</li>
              <li>Combination and combo offers</li>
              <li>Scholarship and discounted enrollments</li>
            </ul>
            
            <p>This policy does not apply to donations, contributions to scholarship funds, or payments made through third-party platforms not directly integrated with Exademy's payment systems. Such transactions are subject to the refund policies of those respective platforms.</p>
          </div>

          {/* Section 2 - 100% Money-Back Guarantee */}
          <div className={styles.refundSection}>
            <h2>2. 100% Money-Back Guarantee</h2>
            
            <h3>2.1 Eligibility Period</h3>
            <p>We are confident in the quality of our courses and services. Therefore, we offer a 100% money-back guarantee for most of our courses and services. Under this guarantee, you are eligible for a full refund if you meet all of the following conditions:</p>
            <ul>
              <li>You request the refund within 7 calendar days from the date of purchase</li>
              <li>You have accessed less than 10% of the total course content</li>
              <li>You have not downloaded, printed, or saved any study materials, PDFs, or resources</li>
              <li>You have not attempted any test in test series products</li>
              <li>You have not attended or scheduled any mentorship session</li>
              <li>You have not violated any of our Terms and Conditions</li>
            </ul>
            
            <h3>2.2 How to Claim</h3>
            <p>To claim your 100% money-back guarantee, you must send a refund request email to refunds@exademy.in from the email address associated with your account. Your email must include:</p>
            <ul>
              <li>Your full name and registered email address</li>
              <li>Order ID or transaction reference number</li>
              <li>Course or service name</li>
              <li>Date of purchase</li>
              <li>Reason for refund request (optional but helpful)</li>
            </ul>
            
            <h3>2.3 Processing Time</h3>
            <p>Approved refunds under the 100% money-back guarantee will be processed within 5-7 business days. The time taken for the refund amount to reflect in your account depends on your payment method and financial institution:</p>
            <ul>
              <li>Credit/Debit cards: 5-7 business days</li>
              <li>UPI: 3-5 business days</li>
              <li>Net banking: 5-7 business days</li>
              <li>EMI: Refund processing may take up to 15 business days as per the terms of the financing partner</li>
              <li>Demand draft: 10-12 business days</li>
            </ul>
            
            <h3>2.4 Exceptions to Money-Back Guarantee</h3>
            <p>The 100% money-back guarantee does not apply to the following products and services:</p>
            <ul>
              <li>Courses purchased more than 7 days ago</li>
              <li>Courses where more than 10% of content has been accessed</li>
              <li>Digital downloads and PDF notes (due to their nature, these are non-refundable)</li>
              <li>Test series after any test has been attempted</li>
              <li>Mentorship sessions after any session has been conducted</li>
              <li>Combination offers where individual components are non-refundable</li>
              <li>Scholarship enrollments with more than 50% fee waiver</li>
              <li>Gift cards and vouchers</li>
            </ul>
          </div>

          {/* Section 3 - Pro-Rata Refunds */}
          <div className={styles.refundSection}>
            <h2>3. Pro-Rata Refunds</h2>
            
            <h3>3.1 Eligibility Period</h3>
            <p>If you request a refund between 8 and 30 days from the date of purchase and have accessed between 10% and 50% of the course content, you may be eligible for a pro-rata refund. Pro-rata refunds are calculated based on the percentage of course content you have accessed and the time elapsed since your enrollment.</p>
            
            <h3>3.2 Refund Calculation</h3>
            <p>Pro-rata refunds are calculated using the following formula:</p>
            <div className={styles.formulaBox}>
              <p><strong>Refund Amount = (Course Fee) - (Course Fee × Percentage of Content Accessed) - (Processing Fee)</strong></p>
            </div>
            <p>Where:</p>
            <ul>
              <li><strong>Percentage of Content Accessed</strong> = (Number of lectures/videos watched ÷ Total number of lectures/videos in the course) × 100</li>
              <li><strong>Processing Fee</strong> = 10% of the course fee or ₹500, whichever is higher</li>
            </ul>
            
            <h3>3.3 Example Calculations</h3>
            <div className={styles.exampleBox}>
              <p><strong>Example 1:</strong> You purchased a course for ₹25,000 and have accessed 20% of the content. Your refund would be calculated as:</p>
              <p>₹25,000 - (₹25,000 × 0.20) - ₹500 = ₹25,000 - ₹5,000 - ₹500 = ₹19,500</p>
            </div>
            <div className={styles.exampleBox}>
              <p><strong>Example 2:</strong> You purchased a course for ₹50,000 and have accessed 35% of the content. Your refund would be calculated as:</p>
              <p>₹50,000 - (₹50,000 × 0.35) - ₹5,000 = ₹50,000 - ₹17,500 - ₹5,000 = ₹27,500</p>
              <p>(Note: Processing fee is 10% of ₹50,000 = ₹5,000, which is higher than ₹500)</p>
            </div>
            
            <h3>3.4 No Refunds After 30 Days</h3>
            <p>No refunds will be provided for any course or service after 30 days from the date of purchase, regardless of the percentage of content accessed. This includes both full refunds and pro-rata refunds.</p>
          </div>

          {/* Section 4 - Course-Specific Refund Policies */}
          <div className={styles.refundSection}>
            <h2>4. Course-Specific Refund Policies</h2>
            
            <h3>4.1 UPSC Mathematics Optional Courses</h3>
            <p>Our UPSC Mathematics Optional courses (2026 and 2027 batches) are long-duration programs with significant study material and lecture hours. The refund policy for these courses is as follows:</p>
            <ul>
              <li><strong>Days 1-7, less than 10% access:</strong> 100% refund minus shipping costs if study materials have been dispatched</li>
              <li><strong>Days 8-30, 10-50% access:</strong> Pro-rata refund as per Section 3.2</li>
              <li><strong>After 30 days:</strong> No refund</li>
              <li><strong>After study materials have been delivered:</strong> Cost of printed materials (as specified in the course breakdown) will be deducted from the refund amount</li>
              <li><strong>Return shipping:</strong> If you have received printed study materials and wish to return them, you must bear the return shipping costs. Materials must be returned in original, undamaged condition within 7 days of refund approval</li>
            </ul>
            
            <h3>4.2 BPSC Mathematics Optional Courses</h3>
            <p>Our BPSC Mathematics Optional courses follow the same refund policy as UPSC courses, with the following additional considerations:</p>
            <ul>
              <li>Bilingual study materials, if shipped, are subject to the same deduction and return policies</li>
              <li>Offline center access fees, if applicable, are non-refundable after 7 days</li>
            </ul>
            
            <h3>4.3 Foundation Courses</h3>
            <p>Our Mathematics Foundation courses are shorter-duration programs. The refund policy is:</p>
            <ul>
              <li><strong>Days 1-7, less than 10% access:</strong> 100% refund</li>
              <li><strong>Days 8-15, 10-30% access:</strong> Pro-rata refund with 10% processing fee</li>
              <li><strong>After 15 days:</strong> No refund</li>
            </ul>
            
            <h3>4.4 CSIR NET and GATE Mathematics Courses</h3>
            <p>These specialized courses have the following refund policy:</p>
            <ul>
              <li><strong>Days 1-7, less than 10% access:</strong> 100% refund</li>
              <li><strong>Days 8-20, 10-40% access:</strong> Pro-rata refund with 10% processing fee</li>
              <li><strong>After 20 days:</strong> No refund</li>
            </ul>
            
            <h3>4.5 Test Series</h3>
            <p>Test series products have a more restrictive refund policy due to their consumable nature:</p>
            <ul>
              <li><strong>Chapter-wise Test Series:</strong> 100% refund within 7 days of purchase if no tests have been attempted. No refund after any test has been attempted.</li>
              <li><strong>Full Syllabus Test Series:</strong> 100% refund within 7 days of purchase if no tests have been attempted. Pro-rata refund within 15 days based on number of tests attempted. No refund after 15 days or after attempting 3 or more tests.</li>
              <li><strong>Combo Test Series Packages:</strong> Refund is calculated based on the individual components. Discount applied at the time of purchase will be proportionally adjusted in the refund amount.</li>
            </ul>
            
            <h3>4.6 Mentorship and Guidance Programs</h3>
            <p>Mentorship programs involve one-on-one time with our experts and have the following refund policy:</p>
            <ul>
              <li><strong>Before any session:</strong> 100% refund within 7 days of purchase</li>
              <li><strong>After 1 session:</strong> 75% refund within 15 days of purchase</li>
              <li><strong>After 2 sessions:</strong> 50% refund within 15 days of purchase</li>
              <li><strong>After 3 or more sessions:</strong> No refund</li>
              <li><strong>After 30 days:</strong> No refund regardless of sessions attended</li>
            </ul>
            
            <h3>4.7 Digital Downloads and PDF Notes</h3>
            <p>Due to the nature of digital products, once downloaded or accessed, they cannot be returned. Therefore:</p>
            <ul>
              <li><strong>No refunds</strong> will be provided for digital downloads, PDF notes, e-books, or any downloadable resources once the download has been initiated or the content has been accessed</li>
              <li>If you experience technical issues accessing your digital purchase, please contact support@exademy.in within 7 days, and we will assist you in resolving the issue</li>
            </ul>
            
            <h3>4.8 Printed Study Materials Only</h3>
            <p>If you purchase printed study materials without an accompanying course:</p>
            <ul>
              <li><strong>Before shipment:</strong> 100% refund</li>
              <li><strong>After shipment but before delivery:</strong> 90% refund (10% deduction for shipping and handling)</li>
              <li><strong>After delivery:</strong> Refund only accepted if materials are returned in original, undamaged condition within 7 days of delivery. Refund amount will be 80% of the purchase price. You bear return shipping costs.</li>
              <li><strong>After 7 days of delivery:</strong> No refund</li>
            </ul>
            
            <h3>4.9 Combination and Combo Offers</h3>
            <p>When you purchase a combination offer or combo package that includes multiple products or services:</p>
            <ul>
              <li>Refunds are calculated based on the individual components eligible for refund</li>
              <li>The discount applied to the combo will be proportionally distributed across the refunded components</li>
              <li>If one component of a combo is non-refundable, the entire combo may become non-refundable at our discretion</li>
              <li>Example: If you purchase a combo of a course (₹20,000) and test series (₹5,000) for a discounted price of ₹22,000, and you are eligible for a refund on the course but not on the test series, your refund will be calculated as: (₹20,000 ÷ ₹25,000) × ₹22,000 = ₹17,600, minus applicable processing fees</li>
            </ul>
          </div>

          {/* Section 5 - Scholarship and Discounted Enrollments */}
          <div className={styles.refundSection}>
            <h2>5. Scholarship and Discounted Enrollments</h2>
            
            <h3>5.1 Merit-Cum-Means Scholarships</h3>
            <p>Students who have been awarded merit-cum-means scholarships are subject to the following refund terms:</p>
            <ul>
              <li><strong>100% fee waiver:</strong> No cash refunds are applicable. If you withdraw from the course, your scholarship will be cancelled and you will not be entitled to any monetary compensation.</li>
              <li><strong>50-99% fee waiver:</strong> Refunds, if eligible, will be calculated on the actual amount paid, not on the original course fee. Processing fees will be calculated on the original course fee.</li>
              <li><strong>Less than 50% fee waiver:</strong> Standard refund policy applies to the amount paid.</li>
            </ul>
            
            <h3>5.2 Early Bird Discounts</h3>
            <p>If you enrolled with an early bird discount:</p>
            <ul>
              <li>Refunds, if eligible, will be calculated on the discounted amount you actually paid</li>
              <li>If you withdraw and later re-enroll, you will not be eligible for the same early bird discount</li>
            </ul>
            
            <h3>5.3 Group Discounts</h3>
            <p>For group enrollments (3 or more students):</p>
            <ul>
              <li>Each student's refund is calculated individually based on their content access and time elapsed</li>
              <li>If one student from a group requests a refund, the group discount applied to that student will be recalculated, and the refund amount will be adjusted accordingly</li>
              <li>The remaining students in the group will not have their discount affected</li>
            </ul>
            
            <h3>5.4 Corporate and Institutional Partnerships</h3>
            <p>If you have been sponsored by your employer or enrolled through an institutional partnership:</p>
            <ul>
              <li>Refunds, if eligible, will be processed to the sponsoring organization, not to the individual student</li>
              <li>Special terms agreed upon in the partnership agreement will take precedence over this policy</li>
            </ul>
          </div>

          {/* Section 6 - EMI and Installment Payments */}
          <div className={styles.refundSection}>
            <h2>6. EMI and Installment Payments</h2>
            
            <h3>6.1 Refunds for EMI Purchases</h3>
            <p>If you purchased a course through EMI (Equated Monthly Installment) options:</p>
            <ul>
              <li>Refunds are processed to the original payment source. The refund amount will be credited to your card/account, and it is your responsibility to manage the EMI repayments with your bank or financial institution.</li>
              <li>Exademy is not responsible for any interest, processing fees, or other charges levied by your bank or financing partner in connection with the EMI transaction.</li>
              <li>The refund amount will be the principal amount paid to Exademy, excluding any interest or fees charged by your bank.</li>
              <li>Processing time for EMI refunds may take up to 15 business days.</li>
            </ul>
            
            <h3>6.2 Installment Payment Plans</h3>
            <p>For students who have opted for installment payment plans directly with Exademy:</p>
            <ul>
              <li><strong>Before second installment:</strong> If you request a refund before your second installment is due, the refund will be calculated on the amount paid, minus applicable processing fees. You will not be required to pay the remaining installments.</li>
              <li><strong>After second installment:</strong> Refunds are calculated on the total amount paid, minus applicable processing fees. Any pending installments become immediately due and will be deducted from the refund amount.</li>
              <li><strong>Default on installments:</strong> If you default on your installment payments, your course access will be suspended. No refunds will be provided for amounts already paid.</li>
            </ul>
          </div>

          {/* Section 7 - Refund Request Process */}
          <div className={styles.refundSection}>
            <h2>7. Refund Request Process</h2>
            
            <h3>7.1 How to Request a Refund</h3>
            <p>To request a refund, please follow these steps:</p>
            <ol>
              <li><strong>Email:</strong> Send a refund request email to refunds@exademy.in from the email address associated with your account.</li>
              <li><strong>Subject Line:</strong> Use the subject line "Refund Request - [Order ID] - [Course Name]"</li>
              <li><strong>Include the following information:</strong>
                <ul>
                  <li>Your full name and registered email address</li>
                  <li>Order ID or transaction reference number</li>
                  <li>Course or service name for which you are requesting a refund</li>
                  <li>Date of purchase</li>
                  <li>Reason for refund request (optional but helpful)</li>
                  <li>Any supporting documents, if applicable</li>
                </ul>
              </li>
            </ol>
            
            <h3>7.2 Alternative Methods</h3>
            <p>If you are unable to email, you can also submit a refund request through:</p>
            <ul>
              <li><strong>Phone:</strong> Call our support team at +91 78478 20335 during business hours (Monday-Friday, 10:00 AM - 6:00 PM IST)</li>
              <li><strong>In-Person:</strong> Visit our centers in Bhagalpur or Patna during business hours</li>
              <li><strong>Contact Form:</strong> Submit a refund request through our website's contact form</li>
            </ul>
            
            <h3>7.3 Refund Request Timeline</h3>
            <p>We acknowledge all refund requests within 24 hours of receipt. Our refund team reviews each request and verifies eligibility based on:</p>
            <ul>
              <li>Date of purchase and time elapsed</li>
              <li>Percentage of course content accessed</li>
              <li>Test series attempts (if applicable)</li>
              <li>Mentorship sessions attended (if applicable)</li>
              <li>Study materials downloaded or shipped</li>
              <li>Compliance with Terms and Conditions</li>
            </ul>
            <p>We aim to communicate our decision within 3-5 business days of receiving your complete refund request. In complex cases, this may extend to 7 business days.</p>
            
            <h3>7.4 Required Information for Verification</h3>
            <p>To process your refund, we may require additional information to verify your identity and purchase:</p>
            <ul>
              <li>A screenshot of the transaction confirmation</li>
              <li>Last four digits of the credit/debit card used for payment</li>
              <li>UPI transaction ID</li>
              <li>Bank statement showing the transaction</li>
              <li>Government-issued ID for in-person refunds</li>
            </ul>
            
            <h3>7.5 Refund Rejection Reasons</h3>
            <p>Your refund request may be rejected for the following reasons:</p>
            <ul>
              <li>Request submitted after the eligible refund period</li>
              <li>Exceeded the allowable content access threshold</li>
              <li>Downloaded or saved course materials</li>
              <li>Attempted tests in test series products</li>
              <li>Attended mentorship sessions</li>
              <li>Violation of Terms and Conditions</li>
              <li>Incomplete or incorrect information provided</li>
              <li>Fraudulent activity detected</li>
            </ul>
          </div>

          {/* Section 8 - Chargebacks and Disputes */}
          <div className={styles.refundSection}>
            <h2>8. Chargebacks and Disputes</h2>
            
            <h3>8.1 What is a Chargeback?</h3>
            <p>A chargeback occurs when you dispute a transaction with your bank or credit card company and request them to reverse the payment. Chargebacks are intended to protect consumers from unauthorized transactions and fraud.</p>
            
            <h3>8.2 Our Chargeback Policy</h3>
            <p>We strongly encourage you to contact us directly before initiating a chargeback. Our refund team is committed to resolving any legitimate concerns you may have. Initiating a chargeback without first attempting to resolve the issue with us:</p>
            <ul>
              <li><strong>Immediate account suspension:</strong> Your access to all Exademy courses and services will be immediately suspended pending investigation</li>
              <li><strong>Permanent account termination:</strong> If the chargeback is upheld, your account will be permanently terminated</li>
              <li><strong>Recovery of fees:</strong> You will be responsible for all chargeback fees, penalties, and administrative costs incurred by Exademy, typically ₹500-₹2,500 per chargeback</li>
              <li><strong>Legal action:</strong> We reserve the right to pursue legal action to recover the disputed amount plus all associated costs and damages</li>
              <li><strong>Reporting to credit bureaus:</strong> We may report unpaid chargebacks to credit reporting agencies</li>
            </ul>
            
            <h3>8.3 Chargeback Reversal</h3>
            <p>If you initiate a chargeback and later wish to reverse it, you must:</p>
            <ul>
              <li>Contact your bank or credit card company and request chargeback reversal</li>
              <li>Provide written confirmation from your bank that the chargeback has been reversed</li>
              <li>Pay any chargeback fees incurred by Exademy (typically ₹500-₹2,500)</li>
            </ul>
            <p>Upon successful chargeback reversal and fee payment, your account may be reinstated at our sole discretion.</p>
            
            <h3>8.4 Dispute Resolution</h3>
            <p>If you disagree with our refund decision, you may escalate your dispute through the following process:</p>
            <ol>
              <li><strong>Internal Review:</strong> Email disputes@exademy.in with your order details and reason for disagreement. Our senior management will review your case within 5-7 business days.</li>
              <li><strong>Grievance Officer:</strong> If unsatisfied with the internal review, you may contact our Grievance Officer at grievance@exademy.in. The Grievance Officer will respond within 30 days as required by law.</li>
              <li><strong>Consumer Forum:</strong> If the dispute remains unresolved, you may approach the appropriate consumer dispute redressal forum as per applicable laws.</li>
            </ol>
          </div>

          {/* Section 9 - Cancellation Policy */}
          <div className={styles.refundSection}>
            <h2>9. Cancellation Policy</h2>
            
            <h3>9.1 Cancellation by You</h3>
            <p>You may cancel your enrollment at any time by contacting our support team. However, cancellation does not automatically entitle you to a refund. Refunds, if applicable, are governed by the refund provisions in this policy. Cancellation without eligibility for refund will result in loss of course access without any monetary compensation.</p>
            
            <h3>9.2 Cancellation by Exademy</h3>
            <p>We reserve the right to cancel any course, reschedule classes, or modify course content at our sole discretion. In the unlikely event that we cancel a course you have enrolled in:</p>
            <ul>
              <li><strong>Full refund:</strong> You will be offered a 100% refund of the amount you paid</li>
              <li><strong>Course transfer:</strong> You may opt to transfer your enrollment to another course of equivalent value</li>
              <li><strong>Credit voucher:</strong> You may opt to receive a credit voucher for the full amount, valid for 12 months from the date of issue</li>
            </ul>
            <p>Our liability in such cases is limited to the amount you paid for the cancelled course. We are not responsible for any consequential losses, including but not limited to preparation time, travel expenses, or opportunity costs.</p>
            
            <h3>9.3 Cancellation Due to Misconduct</h3>
            <p>We reserve the right to cancel your enrollment and terminate your account without refund if you engage in:</p>
            <ul>
              <li>Violation of our Terms and Conditions</li>
              <li>Sharing account credentials or course content with unauthorized users</li>
              <li>Recording, distributing, or pirating our content</li>
              <li>Harassing or abusive behavior towards faculty, staff, or fellow students</li>
              <li>Providing false information during registration or scholarship application</li>
              <li>Any fraudulent or illegal activity</li>
            </ul>
          </div>

          {/* Section 10 - Return of Physical Materials */}
          <div className={styles.refundSection}>
            <h2>10. Return of Physical Materials</h2>
            
            <h3>10.1 Return Process</h3>
            <p>If you have received printed study materials, books, or other physical products and are eligible for a refund that requires the return of these materials, you must:</p>
            <ol>
              <li>Request return authorization from our refund team at refunds@exademy.in</li>
              <li>Receive a Return Authorization Number (RAN) and return shipping address</li>
              <li>Pack the materials securely in their original packaging</li>
              <li>Ship the materials within 7 days of receiving the RAN</li>
              <li>Provide the courier tracking number to refunds@exademy.in</li>
            </ol>
            
            <h3>10.2 Condition of Returned Materials</h3>
            <p>Refunds for returned physical materials are subject to inspection upon receipt. To qualify for a refund, materials must be:</p>
            <ul>
              <li>In original, undamaged condition</li>
              <li>Free from writing, highlighting, or markings</li>
              <li>Complete with all pages, supplements, and accessories</li>
              <li>In the original packaging</li>
            </ul>
            <p>If materials are returned in damaged or used condition, a restocking fee of 25-50% of the material cost may be deducted from your refund, or the refund may be rejected entirely.</p>
            
            <h3>10.3 Return Shipping Costs</h3>
            <p>You are responsible for all return shipping costs unless:</p>
            <ul>
              <li>The return is due to an error on our part (e.g., wrong item shipped, defective product)</li>
              <li>The materials were damaged in transit</li>
              <li>Otherwise specified in your refund approval communication</li>
            </ul>
            <p>We recommend using trackable shipping methods and purchasing shipping insurance for high-value returns. We are not responsible for items lost or damaged during return shipping.</p>
            
            <h3>10.4 Non-Returnable Items</h3>
            <p>The following items cannot be returned and are non-refundable once delivered:</p>
            <ul>
              <li>Opened or used study materials with markings</li>
              <li>Digital downloads and access codes</li>
              <li>Customized or personalized materials</li>
              <li>Items returned after 7 days of delivery</li>
            </ul>
          </div>

          {/* Section 11 - Special Circumstances */}
          <div className={styles.refundSection}>
            <h2>11. Special Circumstances</h2>
            
            <h3>11.1 Medical Emergencies</h3>
            <p>If you are unable to continue your course due to a serious medical condition or hospitalization, we may consider refund requests outside our standard policy. To request consideration:</p>
            <ul>
              <li>Submit your request to refunds@exademy.in with the subject line "Medical Emergency Refund Request"</li>
              <li>Include medical certificates, discharge summaries, or other supporting documentation</li>
              <li>Provide your doctor's contact information for verification</li>
            </ul>
            <p>Each case is evaluated individually. Approval is at our sole discretion and may be partial or conditional.</p>
            
            <h3>11.2 Death of a Student</h3>
            <p>In the unfortunate event of a student's death, we provide a full refund to the legal heirs or estate. Please contact us at refunds@exademy.in with:</p>
            <ul>
              <li>Death certificate</li>
              <li>Proof of relationship or legal heirship</li>
              <li>Bank account details for refund processing</li>
            </ul>
            <p>We extend our deepest condolences and process such requests with utmost sensitivity and priority.</p>
            
            <h3>11.3 Technical Issues</h3>
            <p>If you are unable to access course content due to technical issues on our platform:</p>
            <ul>
              <li>Contact our technical support team immediately at techsupport@exademy.in</li>
              <li>We will make every effort to resolve the issue within 24-48 hours</li>
              <li>If the issue persists beyond 7 days and significantly impairs your ability to access the course, you may be eligible for a pro-rata refund or course extension</li>
              <li>Refunds due to technical issues are evaluated on a case-by-case basis</li>
            </ul>
            
            <h3>11.4 Duplicate Payments</h3>
            <p>If you accidentally make duplicate payments for the same course or service:</p>
            <ul>
              <li>Contact us immediately at refunds@exademy.in with transaction details of both payments</li>
              <li>We will verify the duplicate payment and process a refund for the duplicate amount within 5-7 business days</li>
              <li>No processing fees are charged for duplicate payment refunds</li>
            </ul>
            
            <h3>11.5 Wrong Course Enrollment</h3>
            <p>If you accidentally enrolled in the wrong course:</p>
            <ul>
              <li>Contact us within 48 hours of enrollment at refunds@exademy.in</li>
              <li>We can either:
                <ul>
                  <li>Transfer your enrollment to the correct course of equivalent value</li>
                  <li>Process a refund minus a ₹250 administrative fee</li>
                </ul>
              </li>
              <li>After 48 hours, standard refund policy applies</li>
            </ul>
          </div>

          {/* Section 12 - Taxes and Withholding */}
          <div className={styles.refundSection}>
            <h2>12. Taxes and Withholding</h2>
            
            <h3>12.1 Goods and Services Tax (GST)</h3>
            <p>All course fees include applicable GST as per Indian tax laws. When a refund is processed:</p>
            <ul>
              <li>GST component of the refunded amount is reversed and credited back to you</li>
              <li>We will issue a credit note or revised invoice as required under GST laws</li>
              <li>Processing fees and administrative charges are subject to GST and are non-refundable</li>
            </ul>
            
            <h3>12.2 Tax Deducted at Source (TDS)</h3>
            <p>Under certain circumstances, we may be required to deduct tax at source on refunds. If TDS is applicable:</p>
            <ul>
              <li>The refund amount will be net of TDS as per prevailing tax laws</li>
              <li>We will issue a TDS certificate (Form 16A) for the deducted amount</li>
              <li>You may claim credit for this TDS when filing your income tax returns</li>
            </ul>
          </div>

          {/* Section 13 - Contact Information */}
          <div className={styles.refundSection}>
            <h2>13. Contact Information</h2>
            
            <p>For all refund-related inquiries, requests, and disputes, please contact our Refund Department using the following channels:</p>
            
            <div className={styles.contactBlock}>
              <p><strong>Exademy Education Services</strong></p>
              <p><strong>Refund Department</strong></p>
              <p>&nbsp;</p>
              <p><strong>Email for Refund Requests:</strong> info@exademy.in</p>
              <p><strong>Email for Refund Status:</strong> info@exademy.in</p>
              <p><strong>Email for Disputes:</strong> info@exademy.in</p>
              <p><strong>Email for Grievance Officer:</strong> info@exademy.in</p>
              <p>&nbsp;</p>
              <p><strong>Phone:</strong> +91 78478 20335 (Extension 3 for Refunds)</p>
              <p><strong>Operating Hours:</strong> Monday to Friday, 10:00 AM to 6:00 PM (Indian Standard Time)</p>
              <p>&nbsp;</p>
              <p><strong>Registered Office:</strong></p>
              <p>Bhagalpur, Bihar - 812001, India</p>
              <p>&nbsp;</p>
              <p><strong>Offline Refund Submission:</strong></p>
              <p>Bhagalpur Center</p>
            </div>
            
            <p><strong>Response Time:</strong> We acknowledge all refund-related communications within 24 hours on business days. Refund processing times vary based on the type of refund and payment method as specified in Section 2.3.</p>
          </div>

          {/* Section 14 - Grievance Officer */}
          <div className={styles.refundSection}>
            <h2>14. Grievance Officer for Refunds</h2>
            
            <p>In accordance with applicable laws and regulations, we have appointed a Grievance Officer specifically for refund-related concerns. If your refund issue has not been resolved satisfactorily through our regular refund department, you may escalate it to our Grievance Officer.</p>
            
            <div className={styles.contactBlock}>
              <p><strong>Grievance Officer (Refunds):</strong> [Name]</p>
              <p><strong>Designation:</strong> [Designation]</p>
              <p><strong>Email:</strong> grievance.refunds@exademy.in</p>
              <p><strong>Address:</strong> Exademy Education Services, Bhagalpur, Bihar - 812001, India</p>
              <p><strong>Phone:</strong> +91 78478 20335 (Extension 5)</p>
            </div>
            
            <p>The Grievance Officer will acknowledge receipt of your complaint within 24 hours and endeavor to resolve it within 30 days. When contacting the Grievance Officer, please include all previous correspondence with our refund department and any relevant order details.</p>
          </div>

          {/* Section 15 - Policy Updates and Amendments */}
          <div className={styles.refundSection}>
            <h2>15. Policy Updates and Amendments</h2>
            
            <h3>15.1 Right to Modify</h3>
            <p>We reserve the right to modify, update, or amend this Refund Policy at any time without prior notice. Changes will be effective immediately upon posting on our website and mobile application. Your continued use of our Services after any such changes constitutes your acceptance of the revised policy.</p>
            
            <h3>15.2 Notification of Material Changes</h3>
            <p>For material changes that significantly affect your rights or obligations, we will make reasonable efforts to notify you through:</p>
            <ul>
              <li>Email notification to the address associated with your account</li>
              <li>Prominent notice on our website and mobile application</li>
              <li>Notification through your account dashboard</li>
            </ul>
            
            <h3>15.3 Applicability</h3>
            <p>Unless otherwise stated, all refund requests are governed by the Refund Policy in effect at the time of your purchase. We do not retroactively apply policy changes to previous purchases.</p>
          </div>

          {/* Section 16 - Acknowledgement and Acceptance */}
          <div className={styles.refundSection}>
            <h2>16. Acknowledgement and Acceptance</h2>
            
            <p>By completing a purchase on Exademy, you acknowledge that you have:</p>
            <ul>
              <li>Read and understood this Refund Policy in its entirety</li>
              <li>Agreed to be bound by the terms and conditions outlined herein</li>
              <li>Confirmed that the information provided during purchase is accurate and complete</li>
              <li>Consented to the processing of your personal information as described in our Privacy Policy</li>
            </ul>
            
            <p>You further acknowledge that:</p>
            <ul>
              <li>Course access is a privilege, not a right, and may be revoked in accordance with our Terms and Conditions</li>
              <li>Refunds are processed at our sole discretion based on the eligibility criteria specified in this policy</li>
              <li>This Refund Policy constitutes a legally binding agreement between you and Exademy Education Services</li>
              <li>Any dispute regarding refunds shall be governed by the laws of India and subject to the exclusive jurisdiction of courts in Bhagalpur, Bihar</li>
            </ul>
          </div>

          {/* Acknowledgement Footer */}
          <div className={styles.acknowledgement}>
            <p>This Refund Policy constitutes a legally binding agreement between you and "Exademy" regarding the terms and conditions for refunds on courses, services, and products purchased through our platform.</p>
            <p className={styles.signatureLine}>_________________________________________</p>
            <p>Exademy </p>
            <p className={styles.date}>July 10, 2021</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;